import { useState } from "react";
import { toast } from "sonner";

const EMPTY = {
  razao_social: "", nome_fantasia: "", cnpj: "", cnae: "", grau_risco: "",
  unidade: "", cep: "", endereco: "", numero: "", complemento: "",
  bairro: "", cidade: "", uf: "", responsavel_tecnico: "", logo_base64: "",
};

const onlyDigits = (s) => (s || "").replace(/\D/g, "");

async function fetchCepViaCep(cep) {
  const clean = onlyDigits(cep);
  if (clean.length !== 8) return null;
  const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
  if (!res.ok) return null;
  const data = await res.json();
  if (data.erro) return null;
  return data;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function CompanyForm({ initial, onCancel, onSubmit }) {
  const [form, setForm] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [loadingCep, setLoadingCep] = useState(false);
  const setF = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleCepLookup = async () => {
    const clean = onlyDigits(form.cep);
    if (clean.length !== 8) return;
    setLoadingCep(true);
    try {
      const d = await fetchCepViaCep(clean);
      if (!d) { toast.error("CEP não encontrado."); return; }
      setForm((prev) => ({
        ...prev,
        cep: clean.replace(/^(\d{5})(\d{3})$/, "$1-$2"),
        endereco: d.logradouro || prev.endereco,
        bairro: d.bairro || prev.bairro,
        cidade: d.localidade || prev.cidade,
        uf: d.uf || prev.uf,
      }));
      toast.success("Endereço preenchido");
    } catch {
      toast.error("Falha ao consultar CEP.");
    } finally { setLoadingCep(false); }
  };

  const handleLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Use uma imagem (PNG, JPG, SVG ou WebP)."); return; }
    if (file.size > 1024 * 1024) { toast.error("Logomarca maior que 1MB. Reduza a imagem."); return; }
    const b64 = await fileToBase64(file);
    setF("logo_base64", b64);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit}
          className="bg-white border w-full max-w-3xl p-8 max-h-[92vh] overflow-y-auto"
          data-testid="company-modal">
      <div className="overline mb-2">{initial ? "Editar" : "Nova"} empresa</div>
      <h2 className="font-display text-2xl font-extrabold mb-6 tracking-tight">
        {initial ? "Editar empresa" : "Cadastrar empresa"}
      </h2>

      {/* Logo */}
      <div className="mb-6 border border-slate-200 p-4">
        <label className="label">Logomarca da empresa (opcional, máx. 1MB)</label>
        <div className="flex items-start gap-4 mt-1">
          {form.logo_base64 ? (
            <div className="relative">
              <img src={form.logo_base64} alt="Logomarca"
                   className="w-32 h-32 object-contain border border-slate-200 bg-white" />
              <button type="button" onClick={() => setF("logo_base64", "")}
                      className="absolute -top-2 -right-2 bg-red-600 text-white w-6 h-6 text-xs"
                      data-testid="company-logo-remove">
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          ) : (
            <div className="w-32 h-32 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 bg-slate-50">
              <i className="fa-solid fa-image text-3xl"></i>
            </div>
          )}
          <div className="flex-1">
            <input type="file" accept="image/*" id="company-logo-input"
                   onChange={(e) => handleLogo(e.target.files?.[0])}
                   className="hidden" data-testid="company-logo-input" />
            <label htmlFor="company-logo-input" className="btn btn-secondary text-sm cursor-pointer">
              <i className="fa-solid fa-upload"></i> {form.logo_base64 ? "Trocar" : "Selecionar"} imagem
            </label>
            <p className="text-xs text-slate-500 mt-2">
              Aparecerá na capa do relatório AEP ao lado da logomarca da Sollute.
              Recomendado: PNG/SVG transparente, fundo branco ou claro.
            </p>
          </div>
        </div>
      </div>

      {/* Identification */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="label">Razão Social *</label>
          <input required value={form.razao_social} onChange={(e) => setF("razao_social", e.target.value)}
                 className="input" data-testid="company-field-razao_social" />
        </div>
        <div className="md:col-span-2">
          <label className="label">Nome Fantasia</label>
          <input value={form.nome_fantasia} onChange={(e) => setF("nome_fantasia", e.target.value)}
                 className="input" data-testid="company-field-nome_fantasia"
                 placeholder="Como a empresa é conhecida no mercado" />
        </div>
        <div>
          <label className="label">CNPJ</label>
          <input value={form.cnpj} onChange={(e) => setF("cnpj", e.target.value)}
                 className="input" data-testid="company-field-cnpj" />
        </div>
        <div>
          <label className="label">CNAE</label>
          <input value={form.cnae} onChange={(e) => setF("cnae", e.target.value)}
                 className="input" data-testid="company-field-cnae" />
        </div>
        <div>
          <label className="label">Grau de Risco</label>
          <input value={form.grau_risco} onChange={(e) => setF("grau_risco", e.target.value)}
                 className="input" data-testid="company-field-grau_risco" />
        </div>
        <div>
          <label className="label">Unidade / Filial</label>
          <input value={form.unidade} onChange={(e) => setF("unidade", e.target.value)}
                 className="input" data-testid="company-field-unidade" />
        </div>
        <div className="md:col-span-2">
          <label className="label">Responsável Técnico</label>
          <input value={form.responsavel_tecnico} onChange={(e) => setF("responsavel_tecnico", e.target.value)}
                 className="input" data-testid="company-field-responsavel_tecnico" />
        </div>
      </div>

      {/* Address */}
      <div className="mt-6 pt-6 border-t border-slate-200">
        <div className="overline mb-3">Endereço</div>
        <div className="grid md:grid-cols-12 gap-3">
          <div className="md:col-span-4">
            <label className="label">CEP</label>
            <div className="flex gap-2">
              <input value={form.cep} onChange={(e) => setF("cep", e.target.value)}
                     onBlur={handleCepLookup}
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCepLookup(); } }}
                     placeholder="00000-000"
                     maxLength={9}
                     className="input flex-1" data-testid="company-field-cep" />
              <button type="button" onClick={handleCepLookup} disabled={loadingCep}
                      className="btn btn-secondary text-xs" data-testid="company-cep-lookup">
                {loadingCep ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-magnifying-glass"></i>}
                Buscar
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">Auto-preenche endereço (ViaCEP).</p>
          </div>
          <div className="md:col-span-8">
            <label className="label">Logradouro</label>
            <input value={form.endereco} onChange={(e) => setF("endereco", e.target.value)}
                   className="input" data-testid="company-field-endereco" />
          </div>
          <div className="md:col-span-2">
            <label className="label">Número</label>
            <input value={form.numero} onChange={(e) => setF("numero", e.target.value)}
                   className="input" data-testid="company-field-numero" />
          </div>
          <div className="md:col-span-4">
            <label className="label">Complemento</label>
            <input value={form.complemento} onChange={(e) => setF("complemento", e.target.value)}
                   className="input" data-testid="company-field-complemento" />
          </div>
          <div className="md:col-span-3">
            <label className="label">Bairro</label>
            <input value={form.bairro} onChange={(e) => setF("bairro", e.target.value)}
                   className="input" data-testid="company-field-bairro" />
          </div>
          <div className="md:col-span-3">
            <label className="label">Cidade</label>
            <input value={form.cidade} onChange={(e) => setF("cidade", e.target.value)}
                   className="input" data-testid="company-field-cidade" />
          </div>
          <div className="md:col-span-12">
            <label className="label">UF</label>
            <input value={form.uf} onChange={(e) => setF("uf", e.target.value.toUpperCase().slice(0, 2))}
                   className="input md:max-w-[100px]" data-testid="company-field-uf" maxLength={2} />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-200">
        <button type="button" onClick={onCancel} className="btn btn-secondary" data-testid="company-modal-cancel">Cancelar</button>
        <button type="submit" className="btn btn-primary" data-testid="company-modal-save">
          <i className="fa-solid fa-floppy-disk"></i> Salvar
        </button>
      </div>
    </form>
  );
}
